<script setup lang="ts">
import { useDark, useTimeoutFn } from '@vueuse/core';
import PrimeButton from 'primevue/button';
import PrimeCarousel from 'primevue/carousel';
import ImageCommentsDark from '../assets/images/comments-dark.webp';
import ImageComments from '../assets/images/comments.webp';
import ImageAgent from '../assets/images/roles/agent.webp';
import ImageArchitect from '../assets/images/roles/architect.webp';
import ImageAssetOwner from '../assets/images/roles/asset-owner.webp';
import ImageConsultant from '../assets/images/roles/consultant.webp';
import ImageContractor from '../assets/images/roles/contractor.webp';
import ImageDeveloper from '../assets/images/roles/developer.webp';
import ImageEngineer from '../assets/images/roles/engineer.webp';
import ImageSurveyor from '../assets/images/roles/surveyor.webp';
import Hero from '../components/Hero.vue';
import type { Combine } from '../utils/combine';

const runtimeConfig = useRuntimeConfig();

useSeoMeta({
  title: 'Vismo - The Geospatial Data Platform',
  description: 'Vismo is a geospatial data platform that brings your projects to life',
  ogTitle: 'Vismo - The Geospatial Data Platform',
  ogDescription: 'Vismo is a geospatial data platform that brings your projects to life',
  ogImage: joinPath(runtimeConfig.public.siteUrl, 'og.png'),
  ogUrl: runtimeConfig.public.siteUrl,
  twitterTitle: 'Vismo - The Geospatial Data Platform',
  twitterDescription: 'Vismo is a geospatial data platform that brings your projects to life',
  twitterCard: 'summary_large_image',
  twitterImage: joinPath(runtimeConfig.public.siteUrl, 'twitter.png'),
});

const simplesTitles = [
  'AEC',
  'Asset',
  'Geospatial',
  'BIM',
  'Construction',
  'Survey',
  'Architecture',
  'Site',
];
const simplesTitleIndex = ref(0);
const simplesTitle = computed(() => {
  const title = simplesTitles.at(unref(simplesTitleIndex)) || 'unknown';
  return `${title} Data`;
});
const simplesFadeOut = ref(false);
const simplesVideo = ref<HTMLVideoElement>();

watch(
  simplesVideo,
  (el) => {
    if (el) {
      el.playbackRate = 1.5;
    }
  },
  { immediate: true },
);

function queueNextSimplesTitle() {
  const timeout = 2000 + Math.random() * 1000;

  // fade out old
  useTimeoutFn(() => {
    simplesFadeOut.value = true;
  }, timeout - 200);

  // queue up a fade in for new text
  useTimeoutFn(() => {
    simplesFadeOut.value = false;
    simplesTitleIndex.value = (unref(simplesTitleIndex) + 1) % simplesTitles.length;
    queueNextSimplesTitle();
  }, timeout);
}

queueNextSimplesTitle();

const isDarkMode = useDark();

const commentsImage = computed(() => (unref(isDarkMode) ? ImageCommentsDark : ImageComments));

const combinations: Array<[Combine, Combine]> = [
  [allCombines.maps, allCombines.bim],
  [allCombines.topo, allCombines.bim],
  [allCombines.drone, allCombines.bim],
  [allCombines.drone, allCombines.pointCloud],
  [allCombines.site, allCombines.pointCloud],
  [allCombines.environmental, allCombines.pointCloud],
  [allCombines.environmental, allCombines.hydro],
  [allCombines.drone, allCombines.hydro],
  [allCombines.site, allCombines.hydro],
  [allCombines.monitoring, allCombines.hydro],
  [allCombines.monitoring, allCombines.bim],
  [allCombines.environmental, allCombines.bim],
  [allCombines.site, allCombines.bim],
  [allCombines.utils, allCombines.bim],
  [allCombines.utils, allCombines.pointCloud],
  [allCombines.monitoring, allCombines.pointCloud],
  [allCombines.topo, allCombines.pointCloud],
  [allCombines.maps, allCombines.pointCloud],
];
const combineGreens = Object.values(allCombines).filter((combine) => combine.colour === 'green');
const combineBlues = Object.values(allCombines).filter((combine) => combine.colour === 'blue');

const combineGreenOut = ref(false);
const combineBlueOut = ref(false);
const combineIndex = ref(0);
const combine = computed<[Combine, Combine]>(
  () => combinations.at(unref(combineIndex)) || [allCombines.bim, allCombines.drone],
);

function queueNextCombine() {
  const timeout = 2000 + Math.random() * 1000;

  const currentIndex = unref(combineIndex);
  const nextIndex = (currentIndex + 1) % combinations.length;
  const currentCombine = combinations.at(currentIndex);
  const nextCombine = combinations.at(nextIndex);
  if (!nextCombine || !currentCombine) {
    return;
  }

  // fade out old if changing
  useTimeoutFn(() => {
    combineGreenOut.value = nextCombine[0].id !== currentCombine[0].id;
    combineBlueOut.value = nextCombine[1].id !== currentCombine[1].id;
  }, timeout - 500);

  // queue up a fade in for new text
  useTimeoutFn(() => {
    combineGreenOut.value = false;
    combineBlueOut.value = false;
    combineIndex.value = nextIndex;
    queueNextCombine();
  }, timeout);
}

queueNextCombine();

type UsedBy = { name: string; image: string; description: string };

const usedBy = ref<UsedBy[]>([
  {
    name: 'Surveyors',
    image: ImageSurveyor,
    description:
      'Easily capture, process, and share geospatial data, providing accurate site assessments.',
  },
  {
    name: 'Asset Owners',
    image: ImageAssetOwner,
    description:
      // eslint-disable-next-line vue/max-len
      'Access centralised data to manage, oversee and visualise your portfolio from design to completion.',
  },
  {
    name: 'Consultants',
    image: ImageConsultant,
    description:
      // eslint-disable-next-line vue/max-len
      'Provide expert advice and collaborate on geospatial data to support informed decision-making.',
  },
  {
    name: 'Agents',
    image: ImageAgent,
    description: 'Facilitate property management and transactions with real-time project data.',
  },
  {
    name: 'Developers',
    image: ImageDeveloper,
    description: 'Use geospatial data to plan and visualise development projects effectively.',
  },
  {
    name: 'Contractors',
    image: ImageContractor,
    description:
      'Access the latest geospatial data to ensure accurate and timely project delivery.',
  },
  {
    name: 'Architects',
    image: ImageArchitect,
    description: 'Work with a single source of truth for all design and project data.',
  },
  {
    name: 'Engineers',
    image: ImageEngineer,
    description: 'Track environmental and structural conditions with live geospatial insights.',
  },
]);
</script>

<template>
  <div class="index">
    <section class="index__simples">
      <h1 class="index__simples-title" aria-live="polite">
        The simplest way to manage and visualise your
        <ClientOnly>
          <template #fallback>
            <span class="index__simples-title-spinner">{{ simplesTitles.join(', ') }} Data</span>
          </template>
          <span
            class="index__simples-title-spinner"
            :class="{ 'index__simples-title-spinner--out': simplesFadeOut }"
            ><span
              v-for="(char, index) in simplesTitle.split('')"
              :key="`${simplesTitle}-${index}`"
              class="index__simples-title-spinner-character"
              >{{ char }}</span
            ></span
          >
        </ClientOnly>
      </h1>
      <p class="index__simples-subtitle">
        Your collaborative data hub for plans, drawings, BIM models, site photos, assets and more
      </p>
      <NuxtLink :to="runtimeConfig.public.idUrl" external>
        <PrimeButton>Try It Now 🚀</PrimeButton>
      </NuxtLink>
      <video
        ref="simplesVideo"
        class="index__simples-video"
        width="1920"
        height="1080"
        preload="auto"
        autoplay
        loop
        muted
        disablepictureinpicture
        disableremoteplayback
        playsinline
        aria-label="A showcase of Vismo's visualisation capabilities on various 3D models, CAD drawings and point clouds"
      >
        <source src="../assets/videos/showreel.mp4" type="video/mp4" />
        <source src="../assets/videos/showreel.webm" type="video/webm" />
      </video>
    </section>

    <section class="index__combine">
      <h2 class="index__combine-title">Combine & Visualise Project Data</h2>
      <p class="index__combine-formula" aria-live="polite">
        <ClientOnly>
          <template #fallback>
            <span
              class="index__combine-formula-part"
              style="background: var(--p-primary-color); justify-self: flex-end"
              >{{ combineGreens.map((item) => item.name).join(', ') }}</span
            >
            <span class="index__combine-formula-plus">+</span>
            <span
              class="index__combine-formula-part"
              style="background: var(--p-sky-500); justify-self: flex-start"
              >{{ combineBlues.map((item) => item.name).join(', ') }}</span
            >
          </template>
          <span
            class="index__combine-formula-part"
            style="background: var(--p-primary-color); justify-self: flex-end"
            :style="{
              transform: combineGreenOut ? 'translate(0, 3rem)' : 'translate(0, 0)',
              opacity: combineGreenOut ? 0 : 1,
            }"
            >{{ combine[0].name }}</span
          >
          <span class="index__combine-formula-plus">+</span>
          <span
            class="index__combine-formula-part"
            style="background: var(--p-sky-500); justify-self: flex-start"
            :style="{
              transform: combineBlueOut ? 'translate(0, 3rem)' : 'translate(0, 0)',
              opacity: combineBlueOut ? 0 : 1,
            }"
            >{{ combine[1].name }}</span
          >
        </ClientOnly>
      </p>
      <CombineSvg
        class="index__combine-image"
        :visible="[combine[0].id, combine[1].id]"
        aria-label="Graphic showing how Vismo can combine and visualise different types of AEC project data"
      />
      <p class="index__combine-subtitle">
        Your Data, unified. From CAD drawings to point clouds, unlock the power of smarter,
        contextual decision making.
      </p>
    </section>

    <Hero class="index__storage">
      <template #left>
        <img
          src="../assets/images/storage.svg"
          alt="Multiple users accessing a centralised storage system in Vismo"
          class="index__storage-image"
        />
      </template>
      <template #right>
        <div class="index__storage-content">
          <h2 class="index__storage-title">Simplified Cloud Storage</h2>
          <p class="index__storage-text">
            One source of truth - Keep everyone on the same page with a single, reliable hub for
            project data.
          </p>
        </div>
      </template>
    </Hero>

    <Hero class="index__collaboration" wrap-on="tablet">
      <template #left>
        <div class="index__collaboration-content">
          <h2 class="index__collaboration-title">Easy Collaboration</h2>
          <p class="index__collaboration-text">
            <span class="index__collaboration-text-cut">Cut out long email chains</span> and 👀 lost
            conversations. Keep all communication connected to your work so teams can collaborate
            faster.
          </p>
          <p class="index__collaboration-text">
            📌 Pin comments,
            <span class="index__collaboration-text-highlight">highlight conflicts</span>, and make
            every update visible to the whole team — keeping projects on track.
          </p>
        </div>
      </template>
      <template #right>
        <img
          :src="commentsImage"
          alt="Commenting on a 3D model in Vismo"
          class="index__collaboration-image"
        />
      </template>
    </Hero>

    <section class="index__used-by">
      <h2 class="index__used-by-title">Who Uses Vismo?</h2>
      <p class="index__used-by-text">
        Surveyors, Contractors, Architects, Agents, Developers, Engineers, Asset Owners -
        <span class="index__used-by-text-highlight"
          >anyone who needs to organise and visualise project data.</span
        >
      </p>
      <PrimeCarousel
        class="index__used-by-carousel"
        :value="usedBy"
        :num-visible="4"
        :num-scroll="1"
        :autoplay-interval="5000"
        :responsive-options="[
          {
            breakpoint: '1700px',
            numVisible: 3,
            numScroll: 1,
          },
          {
            breakpoint: '1024px',
            numVisible: 2,
            numScroll: 1,
          },
          {
            breakpoint: '768px',
            numVisible: 1,
            numScroll: 1,
          },
        ]"
        circular
      >
        <template #item="slotProps: { data: UsedBy }">
          <div class="index__used-by-item">
            <img
              class="index__used-by-item-image"
              :src="slotProps.data.image"
              :alt="`Used By ${slotProps.data.name}`"
            />
            <h5 class="index__used-by-item-title">{{ slotProps.data.name }}</h5>
            <p class="index__used-by-item-description">{{ slotProps.data.description }}</p>
          </div>
        </template>
      </PrimeCarousel>
    </section>

    <section class="index__features">
      <h2 class="index__features-title">Why Use Vismo?</h2>
      <p class="index__features-subtitle">
        One platform for smarter, connected geospatial projects.
      </p>

      <div class="index__features-list">
        <IconBox
          icon="extension"
          icon-colour="var(--p-blue-500)"
          title="Open, Connected and Supplier Agnostic"
          text="Works with your existing tools - we take an open, collaborative approach, ensuring compatibility with any data type"
        />

        <IconBox
          icon="lock"
          icon-colour="var(--p-purple-500)"
          title="Security & Simplicity, Built-In"
          text="Secure by Design - your data is protected with enterprise-grade security"
        />

        <IconBox
          icon="devices"
          icon-colour="var(--p-green-500)"
          title="No IT - Free and Easy"
          text="No installs. Sign up and try Vismo today!"
        >
          <NuxtLink
            :to="runtimeConfig.public.idUrl"
            external
            style="align-self: center; margin-top: 1rem"
          >
            <PrimeButton severity="info">Try It Now 🚀</PrimeButton>
          </NuxtLink>
        </IconBox>

        <IconBox
          icon="joystick"
          icon-colour="var(--p-teal-500)"
          text="Skip the CDE complexity and cost without the sacrifice. Start managing your projects in under 5 minutes."
        >
          <template #title>
            <strong class="index__features-item-title-highlight">CDE</strong>asy Mode
          </template>
        </IconBox>

        <IconBox
          icon="database"
          icon-colour="var(--p-purple-500)"
          title="One Place for Everything"
          text="A centralised, always up-to-date home for your project’s data and documents"
        />

        <IconBox
          icon="groups"
          icon-colour="var(--p-yellow-500)"
          title="Seamless Teamwork"
          text="Share files, assign tasks, and collaborate in real time - without email chains"
        />

        <IconBox
          icon="public"
          icon-colour="var(--p-blue-500)"
          title="See Your Data, Your Way"
          text="Explore geospatial data in 2D and 3D for better insights and decision-making"
        />

        <IconBox
          icon="earthquake"
          icon-colour="var(--p-green-500)"
          tag="Coming Soon"
          title="Structural & Live Monitoring"
          text="Keep track of structural integrity over time and monitor real-time sensor data for environmental insights"
        />

        <IconBox
          icon="savings"
          icon-colour="var(--p-pink-500)"
          title="Affordable"
          text="Built for teams of any size - affordable for SMEs, scalable for enterprises"
        />

        <IconBox
          icon="architecture"
          icon-colour="var(--p-fuchsia-500)"
          title="CAD-Ready"
          text="View and manage CAD drawings directly in Vismo - no extra software needed"
        />

        <IconBox
          icon="foundation"
          icon-colour="var(--p-teal-500)"
          title="Big On BIM"
          text="Work seamlessly with BIM models. View, manage, and collaborate on 3D building data with ease"
        />

        <IconBox
          icon="person_celebrate"
          icon-colour="var(--p-yellow-500)"
          title="Join A Growing Community"
          text="VISMO'ers are the coolest bunch around (we might be biased)"
        />
      </div>
    </section>

    <MailingList
      title="Want to find out more?"
      subtitle="Join our mailing list to get the latest updates."
    />

    <Hero class="index__contact">
      <template #left>
        <img
          src="../assets/images/contact.webp"
          alt="Contact Us At Vismo"
          class="index__contact-image"
        />
      </template>
      <template #right>
        <div class="index__contact-content">
          <h2 class="index__contact-title">We want to hear from you</h2>
          <p class="index__contact-text">
            We are always looking to improve our product. If you have any feedback or questions, we
            would love to hear from you.
          </p>
          <NuxtLink :to="`mailto:${runtimeConfig.public.emailInfo}`">
            <PrimeButton label="Get In Touch" />
          </NuxtLink>
        </div>
      </template>
    </Hero>
  </div>
</template>

<style scoped lang="scss">
@use '../assets/styles/_globals.scss' as g;

.index {
  display: flex;
  flex-direction: column;
  gap: 5rem;
  overflow: hidden;

  &__simples {
    width: 100%;
    padding: 3rem 5rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2rem;

    @media (max-width: g.$desktop-breakpoint) {
      padding: 3rem 3rem;
    }

    @media (max-width: g.$mobile-breakpoint) {
      padding: 3rem 2rem;
    }
  }

  &__simples-title {
    font-size: 2.3rem;
    font-weight: 800;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;

    overflow: visible;
    width: 100%;

    @media (max-width: g.$mobile-breakpoint) {
      font-size: 1.8rem;
    }
  }

  &__simples-title-spinner {
    display: block;
    transition: opacity 200ms ease-in-out;
    opacity: 1;

    padding: 0.5rem 1rem;
    display: inline-block;

    white-space: nowrap;

    &--out {
      opacity: 0;
    }
  }

  @keyframes index-simples-title-spinner-character-fade-in {
    from {
      background-position: top left;
      opacity: 0;
    }

    to {
      background-position: top right;
      opacity: 1;
    }
  }

  &__simples-title-spinner-character {
    background: linear-gradient(0.25turn, transparent, 50%, var(--p-primary-color));
    background-position: top left;
    opacity: 0;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    background-size: 500% auto;

    animation-name: index-simples-title-spinner-character-fade-in;
    animation-duration: 0.75s;
    animation-iteration-count: 1;
    animation-timing-function: ease-in-out;
    animation-fill-mode: forwards;

    @for $i from 1 through 20 {
      &:nth-child(#{$i}) {
        animation-delay: #{0.05 * $i}s;
      }
    }
  }

  &__simples-subtitle {
    font-size: 1.2rem;
    font-weight: 300;
  }

  &__simples-video {
    width: 100%;
    max-width: 70rem;
    height: auto;
    border-radius: 1rem;
    box-shadow: 0.2rem 0.4rem 2rem 0
      color-mix(in srgb, light-dark(var(--p-surface-900), var(--p-surface-0)) 10%, transparent);
  }

  &__combine {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2rem;
    padding: 0 5rem 3rem;

    @media (max-width: g.$desktop-breakpoint) {
      padding: 0 3rem 3rem;
    }

    @media (max-width: g.$mobile-breakpoint) {
      padding: 0 2rem 3rem;
    }
  }

  &__combine-title {
    font-size: 2rem;
    font-weight: 800;
  }

  &__combine-formula {
    font-size: 1.5rem;
    font-weight: 700;
    display: grid;
    grid-template-columns: 1fr 0fr 1fr;
    gap: 1rem;
    align-items: center;

    @media (max-width: g.$tablet-breakpoint) {
      font-size: 1rem;
      gap: 0.5rem;
    }
  }

  &__combine-formula-part {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 20rem;
    color: white;
    padding: 0.5rem 1rem;
    text-align: center;
    width: min-content;
    border-radius: 0.5rem;
    transition:
      transform 500ms ease-out,
      opacity 500ms ease-out;
  }

  &__combine-image {
    max-width: 40rem;
  }

  &__combine-subtitle {
    font-size: 1.2rem;
    font-weight: 300;
  }

  &__storage {
    padding: 6rem 5rem;
    --hero-left-width: 40%;
    --hero-right-width: 60%;
    --hero-container-max-width: 60rem;
    background: light-dark(var(--p-surface-0), var(--p-surface-900));

    @media (max-width: g.$desktop-breakpoint) {
      padding: 3rem 3rem;
    }

    @media (max-width: g.$mobile-breakpoint) {
      padding: 3rem 2rem;
    }
  }

  &__storage-content {
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    justify-content: center;

    @media (max-width: g.$tablet-breakpoint) {
      order: -1;
    }
  }

  &__storage-title {
    font-size: 2.3rem;
    font-weight: 800;
  }

  &__storage-text {
    font-size: 1.2rem;
    font-weight: 300;
  }

  &__storage-image {
    display: block;
    width: 100%;
    max-width: 30rem;
    object-fit: cover;

    @media (max-width: g.$tablet-breakpoint) {
      max-width: 25rem;
    }
  }

  &__collaboration {
    padding: 3rem 5rem;
    --hero-left-width: 60%;
    --hero-right-width: 40%;
    --hero-container-max-width: 60rem;

    @media (max-width: g.$desktop-breakpoint) {
      padding: 3rem 3rem;
    }

    @media (max-width: g.$mobile-breakpoint) {
      padding: 3rem 2rem;
    }
  }

  &__collaboration-content {
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    justify-content: center;
  }

  &__collaboration-title {
    font-size: 2.3rem;
    font-weight: 800;
  }

  &__collaboration-text {
    font-size: 1.2rem;
    font-weight: 300;
  }

  &__collaboration-text-cut {
    text-decoration: line-through;
    text-decoration-color: var(--p-primary-color);
    text-decoration-thickness: 1px;
    text-decoration-style: wavy;
  }

  &__collaboration-text-highlight {
    background: light-dark(var(--p-yellow-200), var(--p-yellow-700));
  }

  &__collaboration-image {
    display: block;
    width: 100%;
    max-width: 30rem;
    border-radius: 50%;
    object-fit: cover;

    @media (max-width: g.$tablet-breakpoint) {
      max-width: 25rem;
    }
  }

  &__used-by {
    width: 100%;
    padding: 3rem 5rem;
    background: light-dark(var(--p-surface-0), var(--p-surface-900));
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2rem;

    @media (max-width: g.$desktop-breakpoint) {
      padding: 3rem 2rem;
    }

    @media (max-width: g.$mobile-breakpoint) {
      padding: 3rem 0rem;
    }
  }

  &__used-by-title {
    font-size: 2rem;
    font-weight: 800;
    text-align: center;
  }

  &__used-by-text {
    font-size: 1.2rem;
    font-weight: 300;
    max-width: 50rem;
    text-align: center;

    @media (max-width: g.$mobile-breakpoint) {
      padding: 0 1rem;
    }
  }

  &__used-by-text-highlight {
    font-weight: 700;
    color: var(--p-primary-color);
  }

  &__used-by-carousel {
    width: 100%;
  }

  &__used-by-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    font-weight: 500;
    padding: 1rem;
    text-align: center;
  }

  &__used-by-item-image {
    border-radius: 50%;
    width: 10rem;
  }

  &__used-by-item-title {
    font-size: 1.3rem;
    font-weight: 800;
  }

  &__used-by-item-description {
    line-height: 1.5rem;
    font-weight: 300;
  }

  &__features {
    width: 100%;
    padding: 3rem 5rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2rem;

    @media (max-width: g.$desktop-breakpoint) {
      padding: 3rem 3rem;
    }

    @media (max-width: g.$mobile-breakpoint) {
      padding: 3rem 2rem;
    }
  }

  &__features-title {
    font-size: 2rem;
    font-weight: 800;
  }

  &__features-subtitle {
    font-size: 1.2rem;
    font-weight: 300;
  }

  &__features-list {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-template-rows: repeat(1, max-content);
    gap: 2rem;

    @media (max-width: 1700px) {
      grid-template-columns: repeat(3, 1fr);
      grid-template-rows: repeat(2, max-content);
    }

    @media (max-width: g.$desktop-breakpoint) {
      grid-template-columns: repeat(2, 1fr);
      grid-template-rows: repeat(2, max-content);
    }

    @media (max-width: g.$tablet-breakpoint) {
      display: flex;
      flex-direction: column;
    }
  }

  &__features-item-title-highlight {
    color: var(--p-primary-color);
  }

  &__contact {
    padding: 6rem 5rem;
    --hero-left-width: 40%;
    --hero-right-width: 60%;
    --hero-container-max-width: 60rem;

    @media (max-width: g.$desktop-breakpoint) {
      padding: 3rem 3rem;
    }

    @media (max-width: g.$mobile-breakpoint) {
      padding: 3rem 2rem;
    }
  }

  &__contact-content {
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    justify-content: center;

    @media (max-width: g.$tablet-breakpoint) {
      order: -1;
    }
  }

  &__contact-title {
    font-size: 2.3rem;
    font-weight: 800;
  }

  &__contact-text {
    font-size: 1.2rem;
    font-weight: 300;
  }

  &__contact-image {
    display: block;
    width: 100%;
    max-width: 30rem;
    object-fit: cover;

    @media (max-width: g.$mobile-breakpoint) {
      max-width: 25rem;
    }
  }
}
</style>
